import React, { useEffect, useRef } from 'react';

interface ConsultationFormEmbedProps {
  locale?: string;
  formParams?: Record<string, string | number | undefined>;
}

const FORM_KEY = '1e3d94d1-4436-490b-8c5d-ac78edaccd4f';
const SCRIPT_SRC = 'https://forms.deere.com/forms/embed.js';
const ALLOWED_SCRIPT_ORIGIN = 'https://forms.deere.com';

function ConsultationFormEmbed({
  locale = 'de_DE',
  formParams = {},
}: ConsultationFormEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    let shouldProceed = true;
    try {
      const u = new URL(SCRIPT_SRC);
      const origin = `${u.protocol}//${u.hostname}`;
      if (u.protocol !== 'https:' || origin !== ALLOWED_SCRIPT_ORIGIN) {
        throw new Error('Blocked loading script from non-allowed origin');
      }
    } catch (e) {
      console.error('ConsultationFormEmbed: invalid script URL', e);
      shouldProceed = false;
    }

    if (!shouldProceed || !containerRef.current) return;

    // Create form container div
    const formDiv = document.createElement('div');
    formDiv.textContent = 'Loading...please wait';
    formDiv.setAttribute('data-corpform-id', FORM_KEY);
    formDiv.setAttribute('data-locale', locale);

    // Add all form parameters as data attributes
    Object.entries(formParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formDiv.setAttribute(`data-${key.replace(/_/g, '-')}`, String(value));
      }
    });

    // Clear and append form div to container
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(formDiv);

    // Load script only if not already loaded
    if (
      !scriptLoadedRef.current &&
      !document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    ) {
      scriptLoadedRef.current = true;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => {
        console.log('Forms embed script loaded successfully');
      };
      script.onerror = () => {
        console.error('Failed to load forms embed script');
      };
      document.body.appendChild(script);
    }
  }, [locale, formParams]);

  return <div ref={containerRef} style={{ minHeight: 600 }} />;
}

ConsultationFormEmbed.defaultProps = {
  locale: 'de_DE',
  formParams: {},
};

export default ConsultationFormEmbed;
